import {Component, computed, OnDestroy, signal} from '@angular/core';
import {BreadCrumbs, LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {Router, RouterModule, UrlSegment, UrlSegmentGroup, UrlTree} from '@angular/router';

import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import {filter, map, startWith, take} from 'rxjs/operators';
import {Subscription, switchMap} from 'rxjs';
import {CpOrganization, JanusService} from '../../services/janus.service';

class DummyOAuthEvent extends OAuthEvent {
  constructor() {
    super('silently_refreshed');
  }
}

@Component({
  selector: 'app-organization-select',
  templateUrl: './organization-switcher.component.html',
  styleUrls: ['./organization-switcher.component.scss'],
  imports: [
    LogoAreaComponent,
    HeadlineSectionComponent,
    RouterModule,
  ]
})
export class OrganizationSwitcherComponent implements OnDestroy {

  rest = []

  breadcrumbs: BreadCrumbs = [
    {
      commands: null,
      display: 'Content Platform'
    },
    {
      commands: null,
      display: 'My Organizations'
    }
  ]

  /** Every organization with the link that switches to it. Null while loading. */
  private readonly entries = signal<{ org: CpOrganization, url: UrlTree }[] | null>(null);

  /** Laid out like the Video Toolkit's organization list: a name filter and one sortable column. */
  readonly filter = signal('');
  readonly descending = signal(false);

  readonly total = computed(() => this.entries()?.length ?? 0);
  readonly shown = computed(() => {
    const needle = this.filter().trim().toLowerCase();
    const shown = (this.entries() ?? []).filter(e => !needle || e.org.name.toLowerCase().includes(needle));
    return this.descending() ? [...shown].reverse() : shown;  // entries arrive sorted by name
  });
  readonly loading = computed(() => this.entries() === null);

  subscription: Subscription;

  /**
   * Closes the overlay outlet only, like the other two popovers: the primary outlet keeps whatever the
   * user was on, so this reveals that page rather than navigating away. On a fresh login there is no
   * page underneath yet, so the switcher comes straight back -- an organization still has to be picked.
   */
  protected dismiss(): void {
    this.router.navigate([{outlets: {overlay: null}}], {queryParamsHandling: 'preserve'});
  }

  constructor(janus: JanusService, authService: OAuthService, private router: Router) {

    this.subscription = authService.events.pipe(
      startWith(new DummyOAuthEvent()),
      filter(() => authService.hasValidAccessToken()),
      take(1),
      switchMap(() => janus.userinfo$.pipe(map(JanusService.organizationsOf)))
    ).subscribe(orgs => {
        this.entries.set(orgs.map(o => {
          const tree = this.router.parseUrl(this.router.url);
          const primary = tree.root.children['primary'];
          if (!primary ||
            (primary.segments[0].path !== 'o' || !primary.segments[1].path.startsWith('urn:janus:organization'))) {
            tree.root.children['primary'] = new UrlSegmentGroup([new UrlSegment('o', {}), new UrlSegment(o.id, {})], {})
            delete tree.root.children['overlay'];

            return {
              'org': o,
              'url': tree
            }

          } else {
            const segments = primary.segments.map(s => s.path);

            segments[1] = o.id;
            primary.segments = segments.map(s => new UrlSegment(s, {}));
            delete tree.root.children['overlay'];
            return {
              'org': o,
              'url': tree
            }
          }
        }));
      }
    );
  }

  protected open(url: UrlTree): void {
    this.router.navigateByUrl(url);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }


}
